import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import DesktopNav from './header/DesktopNav';
import MobileNav from './header/MobileNav';

const serviciosSubItems = [
  { id: 'servicios-all', label: 'Todos los Servicios', path: '/servicios' },
  { id: 'servicios-generadores', label: 'Renta de Generadores', path: '/generadores' },
  { id: 'servicios-torres', label: 'Renta de Torres de Iluminación', path: '/servicios/torres-de-iluminacion' },
  { id: 'servicios-soldadoras', label: 'Renta de Soldadoras', path: '/servicios/soldadoras' },
  { id: 'servicios-reparacion', label: 'Reparación y Mantenimiento', path: '/servicios/reparacion-y-mantenimiento' },
];

const navItems = [
  { id: 'inicio', label: 'Inicio', path: '/' },
  { id: 'servicios', label: 'Servicios', path: '/servicios', hasDropdown: true },
  { id: 'generadores', label: 'Generadores', path: '/generadores' },
  { id: 'blog', label: 'Blog', path: '/blog' },
  { id: 'faq', label: 'FAQ', path: '/faq' },
  { id: 'contacto', label: 'Contacto', path: '/contacto' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [serviciosOpen, setServiciosOpen] = useState(false);
  const [mobileServiciosOpen, setMobileServiciosOpen] = useState(false);
  const location = useLocation();
  const serviciosRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (serviciosRef.current && !serviciosRef.current.contains(e.target)) {
        setServiciosOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setServiciosOpen(false);
    setMobileServiciosOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/98 backdrop-blur-md shadow-lg'
        : 'bg-white/95 backdrop-blur-sm'
    } border-b border-gray-200`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_power-equipment/artifacts/wolxjy6h_LOGO%20GENSOL%20SIN%20FONDO.png"
              alt="GENSOL Logo"
              className="h-16 w-auto cursor-pointer transition-transform hover:scale-105"
            />
          </Link>

          <DesktopNav
            navItems={navItems}
            serviciosSubItems={serviciosSubItems}
            isActive={isActive}
            serviciosOpen={serviciosOpen}
            setServiciosOpen={setServiciosOpen}
            serviciosRef={serviciosRef}
          />

          {/* Contact Info - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:+522299212332"
              data-analytics-click="header-phone"
              className="flex items-center space-x-2 text-gray-700 hover:text-orange-500 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">229 921 23 32</span>
            </a>
            <Link to="/contacto">
              <Button
                data-testid="header-cta-quote"
                data-analytics-click="header-quote-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white transition-transform hover:scale-105"
              >
                Cotizar Ahora
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-700 hover:text-orange-500 transition-colors p-2"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <MobileNav
          navItems={navItems}
          serviciosSubItems={serviciosSubItems}
          isActive={isActive}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          mobileServiciosOpen={mobileServiciosOpen}
          setMobileServiciosOpen={setMobileServiciosOpen}
        />
      </div>
    </header>
  );
};

export default Header;
